import { cn } from '../../lib/cn';

/**
 * Icono por tipo de archivo.
 *
 * La familia se deduce del MIME y sólo se cae al nombre cuando el MIME no dice
 * nada: la extensión la escribe el usuario y puede mentir. Es la misma función
 * que decide si algo se puede previsualizar, así que icono y vista previa
 * nunca se contradicen.
 */
export type FileFamily =
  | 'image'
  | 'pdf'
  | 'document'
  | 'spreadsheet'
  | 'presentation'
  | 'video'
  | 'audio'
  | 'archive'
  | 'code'
  | 'unknown';

const POR_EXTENSION: Record<string, FileFamily> = {
  pdf: 'pdf',
  doc: 'document',
  docx: 'document',
  odt: 'document',
  rtf: 'document',
  txt: 'document',
  md: 'document',
  xls: 'spreadsheet',
  xlsx: 'spreadsheet',
  csv: 'spreadsheet',
  ods: 'spreadsheet',
  ppt: 'presentation',
  pptx: 'presentation',
  zip: 'archive',
  rar: 'archive',
  '7z': 'archive',
  tar: 'archive',
  gz: 'archive',
  dwg: 'document',
  dxf: 'document',
};

export function fileFamily(mimeType: string | null, name: string): FileFamily {
  const mime = (mimeType ?? '').toLowerCase();

  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  if (mime === 'application/pdf') return 'pdf';
  if (mime.includes('spreadsheet') || mime.includes('excel')) return 'spreadsheet';
  if (mime.includes('presentation') || mime.includes('powerpoint')) return 'presentation';
  if (mime.includes('word') || mime.startsWith('text/')) return 'document';
  if (mime.includes('zip') || mime.includes('compressed')) return 'archive';

  const extension = name.split('.').pop()?.toLowerCase() ?? '';
  return POR_EXTENSION[extension] ?? 'unknown';
}

const ASPECTO: Record<FileFamily, { glyph: string; tone: string }> = {
  image: { glyph: '🖼', tone: 'bg-primary/15 text-primary-soft' },
  pdf: { glyph: '📕', tone: 'bg-danger/15 text-danger' },
  document: { glyph: '📄', tone: 'bg-primary/15 text-primary-soft' },
  spreadsheet: { glyph: '📊', tone: 'bg-success/15 text-success' },
  presentation: { glyph: '📈', tone: 'bg-warning/15 text-warning' },
  video: { glyph: '🎬', tone: 'bg-secondary/20 text-secondary' },
  audio: { glyph: '🎵', tone: 'bg-secondary/20 text-secondary' },
  archive: { glyph: '🗜', tone: 'bg-warning/15 text-warning' },
  code: { glyph: '⌨', tone: 'bg-surface-2 text-muted' },
  unknown: { glyph: '📁', tone: 'bg-surface-2 text-muted' },
};

/**
 * Formatos que un navegador pinta sin ayuda. El resto se ofrece descargar.
 *
 * El vídeo entra porque el producto lo deja subir: la galería y los álbumes
 * piden `image/*,video/*`, así que abrir un vídeo y encontrarse «no se puede
 * previsualizar, descárgalo» es que el visor se quede corto donde la subida ya
 * llegó. `<video>` es tan nativo como `<img>`, y la URL viene firmada igual.
 */
export function isPreviewable(family: FileFamily): boolean {
  return family === 'image' || family === 'pdf' || family === 'video';
}

export function FileIcon({
  mimeType,
  name,
  size = 'md',
  className,
}: {
  mimeType: string | null;
  name: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}) {
  const family = fileFamily(mimeType, name);
  const { glyph, tone } = ASPECTO[family];
  const dimension = {
    sm: 'h-8 w-8 text-base',
    md: 'h-10 w-10 text-lg',
    lg: 'h-14 w-14 text-2xl',
  }[size];

  return (
    <span
      className={cn('grid shrink-0 place-items-center rounded-xl', dimension, tone, className)}
      aria-hidden="true"
    >
      {glyph}
    </span>
  );
}
